import { useEffect, useState } from "react";
import {
  applyActionCode,
  checkActionCode,
  confirmPasswordReset,
  getAuth,
  verifyPasswordResetCode,
} from "firebase/auth";

import "./AuthAction.css";

const AUTH_MODES = {
  VERIFY_EMAIL: "verifyEmail",
  RESET_PASSWORD: "resetPassword",
  VERIFY_AND_CHANGE_EMAIL: "verifyAndChangeEmail",
  RECOVER_EMAIL: "recoverEmail",
};

function getActionParameters() {
  const params = new URLSearchParams(window.location.search);

  return {
    mode: params.get("mode"),
    oobCode: params.get("oobCode"),
    apiKey: params.get("apiKey"),
    continueUrl: params.get("continueUrl"),
    lang: params.get("lang"),
  };
}

function getSafeContinueUrl(url) {
  if (!url) return null;

  try {
    const parsed = new URL(url);

    // Only allow Bharat Connect URLs.
    if (parsed.origin !== window.location.origin) {
      return null;
    }

    return parsed.href;
  } catch {
    return null;
  }
}

export default function AuthAction() {
  const [params] = useState(getActionParameters);

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  const [email, setEmail] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);

  const [error, setError] = useState("");

  const [processing, setProcessing] = useState(false);

  const auth = getAuth();

  useEffect(() => {
    processAction();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function processAction() {
    const { mode, oobCode } = params;

    if (!mode || !oobCode) {
      setStatus("error");
      setError("This authentication link is incomplete or invalid.");
      return;
    }

    try {
      switch (mode) {
        case AUTH_MODES.VERIFY_EMAIL:
          await handleVerifyEmail(oobCode);
          break;

        case AUTH_MODES.RESET_PASSWORD:
          await handleResetPassword(oobCode);
          break;

        case AUTH_MODES.VERIFY_AND_CHANGE_EMAIL:
          await handleVerifyAndChangeEmail(oobCode);
          break;

        case AUTH_MODES.RECOVER_EMAIL:
          await handleRecoverEmail(oobCode);
          break;

        default:
          setStatus("error");
          setError(`Unsupported authentication action: ${mode}`);
      }
    } catch (err) {
      handleFirebaseError(err);
    }
  }

  async function handleVerifyEmail(oobCode) {
    await checkActionCode(auth, oobCode);
    await applyActionCode(auth, oobCode);

    setStatus("success");
    setMessage("Your email address has been successfully verified.");
  }

  async function handleResetPassword(oobCode) {
    const accountEmail = await verifyPasswordResetCode(auth, oobCode);

    setEmail(accountEmail);

    setStatus("reset-password");
  }

  async function handleVerifyAndChangeEmail(oobCode) {
    const info = await checkActionCode(auth, oobCode);

    await applyActionCode(auth, oobCode);

    setStatus("success");

    if (info.data?.email) {
      setMessage(`Your email address has been changed to ${info.data.email}.`);
    } else {
      setMessage("Your email address has been successfully changed.");
    }
  }

  async function handleRecoverEmail(oobCode) {
    const info = await checkActionCode(auth, oobCode);

    await applyActionCode(auth, oobCode);

    setStatus("success");

    if (info.data?.email) {
      setMessage(`Your email address has been restored to ${info.data.email}.`);
    } else {
      setMessage("Your email address has been successfully restored.");
    }
  }

  async function submitPasswordReset(event) {
    event.preventDefault();

    setError("");

    if (!password) {
      setError("Please enter a new password.");
      return;
    }

    if (password.length < 6) {
      setError("Password must contain at least 6 characters.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setProcessing(true);

    try {
      await confirmPasswordReset(auth, params.oobCode, password);

      setStatus("success");

      setMessage("Your password has been successfully reset.");

      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      handleFirebaseError(err);
    } finally {
      setProcessing(false);
    }
  }

  function handleFirebaseError(err) {
    console.error("Firebase Auth Action Error:", err);

    setStatus("error");

    switch (err?.code) {
      case "auth/expired-action-code":
        setError(
          "This authentication link has expired. Please request a new one."
        );
        break;

      case "auth/invalid-action-code":
        setError(
          "This authentication link is invalid or has already been used."
        );
        break;

      case "auth/user-disabled":
        setError("This account has been disabled.");
        break;

      case "auth/user-not-found":
        setError("The account associated with this link could not be found.");
        break;

      case "auth/weak-password":
        setError(
          "This password is too weak. Please choose a stronger password."
        );
        break;

      case "auth/network-request-failed":
        setError(
          "Network error. Please check your internet connection and try again."
        );
        break;

      default:
        setError(
          err?.message ||
            "Something went wrong while processing this authentication request."
        );
    }
  }

  function goToLogin() {
    const continueUrl = getSafeContinueUrl(params.continueUrl);

    if (continueUrl) {
      window.location.href = continueUrl;
      return;
    }

    window.location.href = "/login";
  }

  function goToChat() {
    window.location.href = "/chat";
  }

  if (status === "loading") {
    return (
      <AuthActionLayout>
        <LoadingState />
      </AuthActionLayout>
    );
  }

  if (status === "error") {
    return (
      <AuthActionLayout>
        <ErrorState message={error} onLogin={goToLogin} />
      </AuthActionLayout>
    );
  }

  if (status === "reset-password") {
    return (
      <AuthActionLayout>
        <div className="auth-action-card">
          <div className="auth-action-icon">🔐</div>

          <h1>Reset your password</h1>

          <p className="auth-action-description">
            Create a new password for your Bharat Connect account.
          </p>

          {email && <div className="auth-action-email">{email}</div>}

          <form className="auth-action-form" onSubmit={submitPasswordReset}>
            <div className="form-field">
              <label htmlFor="password">New password</label>

              <div className="password-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  disabled={processing}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? "Hide" : "Show"}
                </button>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="confirmPassword">Confirm password</label>

              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
                disabled={processing}
              />
            </div>

            {error && <div className="auth-action-error">{error}</div>}

            <button
              type="submit"
              className="primary-button"
              disabled={processing}
            >
              {processing ? "Updating password..." : "Update password"}
            </button>
          </form>

          <p className="auth-action-footer">
            After updating your password, you can sign in again using your new
            password.
          </p>
        </div>
      </AuthActionLayout>
    );
  }

  if (status === "success") {
    return (
      <AuthActionLayout>
        <div className="auth-action-card">
          <div className="success-icon">✓</div>

          <h1>You're all set</h1>

          <p className="auth-action-description">{message}</p>

          <div className="success-actions">
            <button className="primary-button" onClick={goToLogin}>
              Continue to login
            </button>

            <button className="secondary-button" onClick={goToChat}>
              Go to Bharat Connect
            </button>
          </div>
        </div>
      </AuthActionLayout>
    );
  }

  return null;
}

function AuthActionLayout({ children }) {
  return (
    <main className="auth-action-page">
      <div className="auth-action-background" />

      <header className="auth-action-header">
        <button className="brand" onClick={() => (window.location.href = "/")}>
          <div className="brand-logo">BC</div>

          <span>Bharat Connect</span>
        </button>
      </header>

      <section className="auth-action-content">{children}</section>

      <footer className="auth-action-page-footer">
        <span>© {new Date().getFullYear()} Bharat Connect</span>

        <div>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
          <a href="/security">Security</a>
        </div>
      </footer>
    </main>
  );
}

function LoadingState() {
  return (
    <div className="auth-action-card">
      <div className="loading-spinner" />

      <h1>Processing...</h1>

      <p className="auth-action-description">
        Please wait while we securely process your authentication request.
      </p>
    </div>
  );
}

function ErrorState({ message, onLogin }) {
  return (
    <div className="auth-action-card">
      <div className="error-icon">!</div>

      <h1>Link unavailable</h1>

      <p className="auth-action-description">{message}</p>

      <button className="primary-button" onClick={onLogin}>
        Go to login
      </button>

      <p className="auth-action-footer">
        If you requested this email recently, request a new link and try again.
      </p>
    </div>
  );
}
